import React from 'react';
import { Grid, Typography, Popover } from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';

import { BaseTable } from 'containers/application-manager/base-table/base-table';
import { useDispatch } from 'react-redux';
import { notifyError, notifySuccess } from 'actions/action-notifications';
import { UserUtils } from 'utils/user-utils';
import { UserPermissions } from 'interfaces/user/Permissions';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { NumberOption } from 'interfaces/forms/types';
import { IState } from 'interfaces/redux/IState';

import { IPopoverStateBase } from 'containers/application-manager/gtd/types';

import { FieldTypes } from 'containers/application-manager/types';
import { IHeaderCell } from 'containers/application-manager/base-table/types';
import { styles } from '../index.styles';
import { dateConvert, resourceTypeFormatter } from '../utils';
import { AddNewSmsTemplateModal } from '../modal/add-new-sms-template-modal';

interface ITableProps {
  searchTerm: string | undefined;
  triggerReload: Date | undefined;
  searchTermByCategory: string | undefined;
  handleReloadDataTrigger: (date: Date | undefined) => void;
  userHasPermissionToEditSmsTemplate: boolean;
  activeCategory: boolean;
  activeItem: boolean;
  activeOnlyItem: boolean;
  categoryOptionsList: NumberOption[];
  customerIds?: any[];
  categories?: any[];
}

type IPopoverState = IPopoverStateBase<any, any>;

const initialPopoverState = { open: false } as IPopoverState;

export const DataTable = (props: any): JSX.Element => {
  const classes: any = styles();

  // # region redux state
  const allSmsPredefinedMessages = useTypedSelector(
    (state: IState) => state.lookups.smsPredefinedMessages,
  );
  const tagTypeLookup = useTypedSelector((state: IState) => state.lookups.patientNotesTagTypes);
  const customers = useTypedSelector(state => state.filters.customers);

  const allCustomers = customers.map<NumberOption>(x => ({
    label: x.name,
    value: x.id,
  }));

  // # endregion

  // #region component state
  const dispatch = useDispatch();
  const [editModalOpen, setEditModalOpen] = React.useState<boolean>(false);
  const [editSmsTemplate, setEditSmsTemplate] = React.useState<any | undefined>(undefined);
  // #endregion

  // # region helper functions
  const getSmsTemplateData = React.useMemo<any>(() => {
    let results: any[] = [];
    let totalCount = 0;
    if (allSmsPredefinedMessages && allSmsPredefinedMessages.length > 0) {
      results = allSmsPredefinedMessages;
      // results = [...results, ...allSmsPredefinedMessages];
      totalCount = allSmsPredefinedMessages.length;
    }
    if (props.customerIds && props.customerIds.length > 0) {
      results = results.filter(r => props.customerIds.some((id: any) => id === r.customer_id));
    }
    if (props.categories && props.categories.length > 0) {
      const categoryVals = props.categories.map((c: any) => c.value);
      results = results.filter(r => categoryVals.some((c: any) => c === r.resource_type.id));
    }
    if (props.searchTerm && props.searchTerm.length > 0) {
      results = results.filter(
        r => r.content.includes(props.searchTerm) || r.name.includes(props.searchTerm),
      );
    }
    return {
      totalCount: results.length,
      results: results,
    };
  }, [allSmsPredefinedMessages, props.categories, props.customerIds, props.searchTerm]);

  const renderEditSmsTemplateModal = (): JSX.Element => {
    if (!editModalOpen) {
      return <></>;
    }

    return (
      <AddNewSmsTemplateModal
        open
        onCancel={() => setEditModalOpen(false)}
        onSuccess={() => props.handleReloadDataTrigger(new Date())}
        initialValue={editSmsTemplate}
        editing
      />
    );
  };

  // #endregion

  // # region constants
  const COLUMN_SETTINGS: IHeaderCell<any, any>[] = [
    {
      label: 'Customer',
      parentKey: 'customer_id',
      sortable: true,
      hideLeftPadding: true,
      parentValueFormatter: (value: unknown) => {
        if (value === -1) {
          return 'All Customers';
        }
        if (value) {
          const customerArr = allCustomers.filter(c => c.value === value);
          if (customerArr && customerArr.length > 0) {
            return customerArr[0].label;
          }
        }
        return '-';
      },
    },
    {
      label: 'Template Name',
      parentKey: 'name',
      sortable: true,
      hideLeftPadding: true,
    },
    {
      label: 'Category',
      parentKey: 'resource_type',
      hideLeftPadding: true,
      sortable: true,
      parentValueFormatter: resourceTypeFormatter,
    },
    {
      label: 'Message Preview',
      parentKey: 'content',
      sortable: true,
    },
    {
      label: 'Created by',
      parentKey: 'created_by_username',
      sortable: true,
    },
    {
      label: 'Last modified',
      parentKey: 'updated_dt',
      sortable: true,
      parentValueFormatter: dateConvert,
    },
  ];
  // #endregion

  const columnSettings = React.useMemo(() => {
    if (props.userHasPermissionToEditSmsTemplate) {
      return COLUMN_SETTINGS.concat({
        sortable: true,
        parentComponent: parent => {
          const parentObj = {
            ...parent,
            resource_type: {
              label: parent && parent.resource_type ? `${parent.resource_type.name}` : null,
              value: parent && parent.resource_type ? `${parent.resource_type.id}` : null,
            },
          };
          return (
            <>
              <div
                onClick={e => {
                  e.stopPropagation();
                  setEditSmsTemplate(parentObj);
                  setEditModalOpen(true);
                }}
              >
                <CreateIcon />
              </div>
            </>
          );
        },
      });
    }
    return COLUMN_SETTINGS;
  }, [props.userHasPermissionToEditSmsTemplate]);
  // #endregion

  return (
    <>
      {props.userHasPermissionToEditSmsTemplate ? renderEditSmsTemplateModal() : null}
      <BaseTable<any, any, any>
        actionsPermitted={props.userHasPermissionToEditSmsTemplate}
        enableParentCheckboxes={false}
        enableChildCheckboxes={UserUtils.userIsPermitted(
          UserPermissions.ApplicationManagerCpmpEdit,
        )}
        orderByDefaultParent="type_name"
        dataSet={getSmsTemplateData}
        paginationQueryParamSettings={{
          pageSizeQueryStringKey: 'pageSize',
          pageNumberQueryStringKey: 'pageNumber',
          searchTermQueryStringKey: 'searchTerm',
          sortPropQueryStringKey: 'sortProp',
          sortOrderQueryStringKey: 'sortOrder',
        }}
        childPkSelector={c => c.name}
        parentPkSelector={p => p.id}
        columnSettings={columnSettings}
        searchTerm={JSON.stringify({
          type: 'MULTIPLE',
          search: [
            {
              values: props.searchTermByCategory,
              columns: ['name'],
              fullMatch: false,
              active: props.activeCategory,
            },
            {
              values: props.searchTerm,
              columns: ['category'],
              fullMatch: false,
              active: props.activeItem,
            },
            {
              values: props.searchTerm,
              columns: ['name', 'category'],
              fullMatch: false,
              active: props.activeOnlyItem,
            },
          ],
        })}
        classesOverride={{
          lastCell: classes.lastCell,
        }}
        triggerReload={props.triggerReload}
      />
    </>
  );
};
